import { useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  ChevronRight,
  FileText,
  Heart,
  HelpCircle,
  LogOut,
  MessageCircle,
  Settings as SettingsIcon,
  User,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Global user menu — Airbnb-style cleaner structure.
 *
 * Top identity block (photo + name, tappable → /profile/me)
 *   ─────
 * Primary: Saved homes (badge), Alerts (badge), Messages (badge)
 *   ─────
 * Secondary: My profile, My documents
 *   ─────
 * Account: Settings (single consolidated page), Help
 *   ─────
 * Log out
 *
 * Intentionally removed per product spec:
 *   • "Edit profile" (opened from /profile/me via Edit button instead)
 *   • "Trust Score" as a nav entry (lives prominently on /profile/me)
 *   • Separate "Privacy settings" (merged into /settings)
 *   • Roommate shortcuts (ship when matching launches)
 */
const MenuItem = ({ to, label, icon: Icon, badge, onNavigate, testId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === to;
  const handle = (e) => {
    e.preventDefault();
    onNavigate();
    navigate(to);
  };
  return (
    <a
      href={to}
      onClick={handle}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors relative ${
        isActive ? 'bg-amber-50' : 'hover:bg-slate-50'
      }`}
      data-testid={testId}
    >
      {isActive && (
        <span
          className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-amber-500"
          aria-hidden
        />
      )}
      <Icon
        className={`h-[18px] w-[18px] flex-shrink-0 ${
          isActive ? 'text-amber-600' : 'text-slate-500'
        }`}
      />
      <span
        className={`flex-1 text-sm ${
          isActive ? 'font-semibold text-amber-800' : 'font-medium text-slate-800'
        }`}
      >
        {label}
      </span>
      {badge ? (
        <span
          className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 bg-amber-500 text-white text-[11px] font-semibold rounded-full"
          data-testid={`${testId}-badge`}
        >
          {badge}
        </span>
      ) : (
        <ChevronRight className="h-4 w-4 text-slate-300" />
      )}
    </a>
  );
};

const Divider = () => <div className="my-2 mx-4 h-px bg-slate-100" />;

const UserMenu = ({ variant, onClose }) => {
  const { logout } = useAuth();
  const { bundle } = useProfile();
  const navigate = useNavigate();

  const savedCount = bundle?.counts?.saved_listings || 0;
  const alertsCount = bundle?.counts?.saved_searches || 0;
  const messagesCount = bundle?.counts?.unread_messages || 0;

  const u = bundle?.user || {};
  // For display: prefer first_name + last portion of `name`, otherwise fall
  // back to email local-part. Never show the raw email in the identity block.
  const displayName = (() => {
    const first = u.preferred_name || u.first_name || '';
    const rest = (u.name || '').split(' ').slice(1).join(' ');
    const full = [first, rest].filter(Boolean).join(' ').trim();
    if (full) return full;
    return (u.email || '').split('@')[0] || 'Your account';
  })();
  const initial = (u.first_name || u.email || '?').charAt(0).toUpperCase();
  const photoUrl = bundle?.profile_photo_token
    ? `${API}/api/files/${bundle.profile_photo_token}`
    : null;

  const doLogout = async () => {
    onClose();
    await logout();
    navigate('/');
  };

  const openProfile = (e) => {
    e.preventDefault();
    onClose();
    navigate('/profile/me');
  };

  return (
    <div
      className="flex flex-col h-full"
      data-testid="user-menu-body"
    >
      {variant === 'drawer' && (
        <div className="flex items-center px-4 py-3 border-b border-slate-100">
          <span className="font-heading font-bold text-base text-slate-900">Menu</span>
          {/* Shadcn SheetContent renders its own close (X) button top-right. */}
        </div>
      )}

      {/* Identity block — tappable, routes to /profile/me. */}
      <a
        href="/profile/me"
        onClick={openProfile}
        className="flex items-center gap-3 px-4 py-4 hover:bg-slate-50 transition-colors"
        data-testid="menu-identity-block"
      >
        <div className="h-11 w-11 rounded-full overflow-hidden bg-amber-500 flex items-center justify-center flex-shrink-0 shadow-soft">
          {photoUrl ? (
            <img src={photoUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-white font-heading font-bold text-base">{initial}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-heading font-bold text-slate-900 truncate" data-testid="menu-identity-name">
            {displayName}
          </p>
          <p className="text-xs text-amber-700 font-medium">View profile</p>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-300" />
      </a>
      <Divider />

      {/* Primary — the daily actions */}
      <div className="px-2">
        <MenuItem
          to="/saved"
          label="Saved homes"
          icon={Heart}
          badge={savedCount > 0 ? savedCount : null}
          onNavigate={onClose}
          testId="menu-saved"
        />
        <MenuItem
          to="/alerts"
          label="Alerts"
          icon={Bell}
          badge={alertsCount > 0 ? alertsCount : null}
          onNavigate={onClose}
          testId="menu-alerts"
        />
        <MenuItem
          to="/messages"
          label="Messages"
          icon={MessageCircle}
          badge={messagesCount > 0 ? messagesCount : null}
          onNavigate={onClose}
          testId="menu-messages"
        />
      </div>
      <Divider />

      {/* Secondary — manage your stuff */}
      <div className="px-2">
        <MenuItem to="/profile/me" label="My profile" icon={User} onNavigate={onClose} testId="menu-profile" />
        <MenuItem to="/profile/documents" label="My documents" icon={FileText} onNavigate={onClose} testId="menu-documents" />
      </div>
      <Divider />

      {/* Account */}
      <div className="px-2">
        <MenuItem to="/settings" label="Settings" icon={SettingsIcon} onNavigate={onClose} testId="menu-settings" />
        <MenuItem to="/help" label="Help" icon={HelpCircle} onNavigate={onClose} testId="menu-help" />
      </div>
      <Divider />

      {/* Logout */}
      <div className="px-2 pb-4">
        <button
          onClick={doLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-50 transition-colors group"
          data-testid="menu-logout"
        >
          <LogOut className="h-[18px] w-[18px] text-slate-500 group-hover:text-red-600" />
          <span className="flex-1 text-left text-sm font-medium text-slate-800 group-hover:text-red-700">
            Log out
          </span>
        </button>
      </div>
    </div>
  );
};

export default UserMenu;
